import { useQueries } from '@tanstack/react-query'
import { useEffect, useMemo, useRef } from 'react'
import { SCHEDULE_USE_API } from '../config'
import ScheduleAPI from '../api/ScheduleAPI'
import { SCHEDULE_QUERY, ScheduleWeekDto } from '../api/ScheduleAPI.model'
import {
  weekDtoToStore,
  serializeColors,
  serializeWeek,
  shiftsInWeek
} from '../api/scheduleMapper'
import {
  markWeekReadThisSession,
  wasWeekReadThisSession
} from '../api/sessionWeeks'
import { useScheduleStore } from '../store/useScheduleStore'

/**
 * Weeks already adopted from the server, so a refetch (window focus, cache
 * revalidation) can't overwrite edits the user has made since.
 *
 * Module-level rather than per-hook: more than one component asks for the same
 * week, the schedule page and the export modal overlap by design, and a
 * per-instance guard would let the second one to mount replace a week the user
 * has unsaved edits in. Keyed by restaurant so it cannot leak across venues.
 */
const hydratedWeeks = new Set<string>()

/**
 * Restaurants whose colour map has been adopted from the server.
 *
 * Colours are restaurant-wide, so every week GET returns the same list. Taking
 * it from each newly fetched week would put the stored value back over a
 * colour the user has just picked and not yet saved, and since the dirty
 * check covers colours too, the week would quietly go clean and the Save
 * button grey out. Once per restaurant is all the payload can tell us anyway.
 */
const restaurantsWithColors = new Set<string>()

const restaurantKey = (): string =>
  localStorage.getItem('restaurantId') ?? 'unknown'

const hydrationKey = (weekStart: string): string =>
  `${restaurantKey()}|${weekStart}`

/**
 * Hydrates whole weeks of the roster from the backend into the local store, and
 * records each as its own save baseline so a freshly loaded week reads as clean.
 *
 * Inert while `SCHEDULE_USE_API` is false: no query runs and the store keeps
 * whatever localStorage holds, which is the offline-first mode.
 */
export const useScheduleWeeksQuery = (weekStarts: string[]) => {
  const replaceWeek = useScheduleStore(state => state.replaceWeek)
  const markWeekSaved = useScheduleStore(state => state.markWeekSaved)
  const markColorsSaved = useScheduleStore(state => state.markColorsSaved)

  const results = useQueries({
    queries: weekStarts.map(weekStart => ({
      queryKey: [
        SCHEDULE_QUERY.WEEK,
        localStorage.getItem('restaurantId'),
        weekStart
      ],
      queryFn: () => ScheduleAPI.getWeek(weekStart),
      enabled: SCHEDULE_USE_API && !!weekStart
    }))
  })

  // useQueries hands back a fresh array every render, so the effect below keys
  // off which weeks have actually arrived rather than off the array identity.
  const loaded = useRef<Array<{ weekStart: string; data?: ScheduleWeekDto }>>(
    []
  )
  loaded.current = weekStarts.map((weekStart, index) => ({
    weekStart,
    data: results[index]?.data
  }))

  const loadedKey = loaded.current
    .filter(entry => entry.data)
    .map(entry => entry.weekStart)
    .join('|')

  useEffect(() => {
    loaded.current.forEach(({ weekStart, data }) => {
      if (!data || hydratedWeeks.has(hydrationKey(weekStart))) return

      // A shift with no remoteId has never reached the server, so replacing
      // this week would destroy it, that is what "copy to next week" leaves
      // behind, and what a shift added to a week the user then navigated away
      // from looks like. Their copy stays and stays saveable; the server's
      // version of that week is adopted only once it has been written.
      //
      // Deliberately NOT recorded as hydrated: the block is a property of what
      // the store holds right now, not of the week. Recording it made the skip
      // permanent, so a week that once held an unsaved shift never showed the
      // server's roster again, and a shift the backend cannot store at all
      // (an overnight one, see useScheduleSave) blocked that week for good.
      const held = shiftsInWeek(useScheduleStore.getState().shifts, weekStart)

      if (held.some(shift => !shift.remoteId)) return

      const { shifts, colorsByUserId } = weekDtoToStore(data)
      // Colours are restaurant-wide, so only the first week to arrive carries
      // them; the rest would re-apply the same map.
      const isFirstColorsRead = !restaurantsWithColors.has(restaurantKey())

      replaceWeek(weekStart, shifts, isFirstColorsRead ? colorsByUserId : {})
      hydratedWeeks.add(hydrationKey(weekStart))
      markWeekReadThisSession(weekStart)

      const state = useScheduleStore.getState()
      markWeekSaved(weekStart, serializeWeek(state.shifts, weekStart))

      // Only when this read actually brought colours back, and recorded as the
      // SERVER's map rather than the merged one. Recording unconditionally
      // marked whatever the store happened to hold as saved, including a
      // colour the user had just picked and that was still inside the quiet
      // period, which then never got written. Recording the merged map instead
      // would do the same to any local colour the server does not know yet:
      // compared against the merged store it reads dirty, so it is pushed.
      if (isFirstColorsRead) {
        restaurantsWithColors.add(restaurantKey())
        markColorsSaved(serializeColors(colorsByUserId))
      }
    })
  }, [loadedKey, replaceWeek, markWeekSaved, markColorsSaved])

  // A week whose GET failed is NOT an empty week. react-query reports a
  // settled-with-error query as "not loading", so without this the caller
  // cannot tell the two apart and would report a month that is silently short
  // a week, which is exactly what the multi-week fetch was added to prevent.
  const failedWeeks = weekStarts.filter((_, index) => results[index]?.isError)

  // A 403 is worth telling apart from a network failure: these endpoints are
  // guarded by read:shifts, which is granted by a seed the deploy pipeline
  // does not run, so "reload and try again" is the wrong advice, nothing the
  // user can do from this page will change the answer.
  const isForbidden = results.some(
    result =>
      (result.error as { response?: { status?: number } } | null)?.response
        ?.status === 403
  )

  /*
    Only a week that has never been on screen reports as loading.

    Saving drops that week's cached GET (see useScheduleSave), which leaves its
    observer with no data and therefore pending again — so every save flashed
    the whole roster back to its skeleton, even though the store still held the
    week and could render it. A placeholder is for having nothing to show; here
    there is something to show, and it is already correct.
  */
  const isFirstLoad = results.some(
    (result, index) =>
      result.isLoading && !wasWeekReadThisSession(weekStarts[index])
  )

  return {
    isLoading: SCHEDULE_USE_API && isFirstLoad,
    failedWeeks,
    isForbidden
  }
}

/** The single-week case, which is what the editing views ask for. */
export const useScheduleQuery = (weekStart: string) => {
  const weekStarts = useMemo(() => [weekStart], [weekStart])
  return useScheduleWeeksQuery(weekStarts)
}
